import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import type { IconType } from 'react-icons';
import { dashboardTransitions } from 'lib/motion/dashboardMotion';

interface AppHeaderIconsProps {
  menuItems: AppHeaderMenuItem[];
  userName?: string;
  userEmail?: string;
  userInitial?: string;
}

export interface AppHeaderMenuItem {
  id: string;
  label: string;
  icon: IconType;
  onClick: () => void | Promise<void>;
  dividerBefore?: boolean;
}

const renderIcon = (Icon: IconType, className: string) => {
  return Icon({ className });
};

const AppHeaderIcons: React.FC<AppHeaderIconsProps> = ({ menuItems, userName, userEmail, userInitial }) => {
  const [openDropdown, setOpenDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!dropdownRef.current?.contains(target)) {
        setOpenDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMenuItemClick = (item: AppHeaderMenuItem) => {
    setOpenDropdown(false);
    void item.onClick();
  };

  const avatarInitial = (userInitial ?? userName?.[0] ?? userEmail?.[0] ?? 'A').toUpperCase();

  return (
    <div className="flex items-center">
      <div className="relative" ref={dropdownRef}>
        <motion.button
          type="button"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D9DEFF] bg-white text-sm font-semibold text-slate-700 shadow-sm transition-colors duration-200 hover:border-[#B7C1FF] hover:bg-[#F7F8FF]"
          title="Account menu"
          onClick={() => setOpenDropdown((current) => !current)}
          aria-haspopup="menu"
          aria-expanded={openDropdown}
          whileTap={prefersReducedMotion ? undefined : { scale: 0.97 }}
          transition={prefersReducedMotion ? { duration: 0 } : dashboardTransitions.interaction}
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#5B6CFF] via-[#6D5EF7] to-[#8A56F0] text-sm font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]">
            {avatarInitial}
          </span>
          <span className="sr-only">Open account menu</span>
        </motion.button>

        <AnimatePresence>
          {openDropdown && (
            <motion.div
              className="absolute right-0 z-50 mt-3 w-80 max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 shadow-[0_18px_45px_rgba(15,23,42,0.12)] backdrop-blur"
              initial={prefersReducedMotion ? false : { opacity: 0, y: -8, scale: 0.98 }}
              animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
              exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.98 }}
              transition={prefersReducedMotion ? { duration: 0.01 } : dashboardTransitions.dropdownOpen}
            >
              <div className="border-b border-slate-200 bg-slate-50/80 px-4 py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-[#5B6CFF] via-[#6D5EF7] to-[#8A56F0] text-base font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]">
                    {avatarInitial}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900">{userName ?? 'Account'}</p>
                    <p className="break-all text-xs leading-5 text-slate-500">{userEmail ?? 'No email available'}</p>
                  </div>
                </div>
              </div>

              <div className="p-2">
                {menuItems.map((item) => (
                  <React.Fragment key={item.id}>
                    {item.dividerBefore && <div className="my-2 border-t border-slate-200" />}
                    <motion.button
                      type="button"
                      onClick={() => handleMenuItemClick(item)}
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-100 hover:text-slate-900"
                      whileHover={prefersReducedMotion ? undefined : { x: 1 }}
                      transition={prefersReducedMotion ? { duration: 0 } : dashboardTransitions.dropdownItem}
                    >
                      {renderIcon(item.icon, 'h-4 w-4 text-slate-400')}
                      <span>{item.label}</span>
                    </motion.button>
                  </React.Fragment>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AppHeaderIcons;
